"use client";

import { ChevronDownIcon } from "@/components/event-detail/chevron-down-icon";
import { SmartImage } from "@/components/ui/smart-image";
import { getBetTypeDisplay } from "@/constants/bet-type-style";
import { useThemeMode } from "@/features/theme/theme-provider";
import { getBrandLogoIcon, getBrandLogoIconDark } from "@/lib/brand-config";
import { cn } from "@/lib/utils";
import { calc, get } from "@/utils/utils";
import { Loader2 } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useCommentInput } from "./hooks";
import PositionSelectDrawer from "./position-select-drawer";
import { useCommentsStore } from "./store";

interface CommentInputProps {
    eventData?: { eventImageUrl?: string; eventTitle: string };
    matchups?: any[];
}

const COMMENT_TEXTAREA_MAX_HEIGHT = 85;

function DropdownArrow() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-6"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M5.29297 9.70703C4.90245 9.31651 4.90245 8.68349 5.29297 8.29297C5.68349 7.90245 6.31651 7.90245 6.70703 8.29297L12 13.5859L17.293 8.29297C17.6835 7.90245 18.3165 7.90245 18.707 8.29297C19.0976 8.68349 19.0976 9.31651 18.707 9.70703L12.707 15.707C12.3165 16.0976 11.6835 16.0976 11.293 15.707L5.29297 9.70703Z"
                fill="black"
                fillOpacity="0.9"
            />
        </svg>
    );
}

export default function CommentInput( { eventData, matchups }: CommentInputProps ) {
    const eventNo = useCommentsStore( ( state ) => state.eventNo );
    const {
        content,
        remainingChars,
        handleChange,
        handlePost,
        isPosting,
        userGuessOptions,
        selectedGuessOption,
        handleSelectGuessOption,
        shouldShowGuessSelector,
    } = useCommentInput( eventNo || "" );

    const [ drawerOpen, setDrawerOpen ] = useState( false );
    const [ isFocused, setIsFocused ] = useState( false );
    const textareaRef = useRef<HTMLTextAreaElement>( null );
    const { themeMode } = useThemeMode();
    const fallbackCoverImage = themeMode === "dark" ? getBrandLogoIconDark() : getBrandLogoIcon();
    const coverImage = eventData?.eventImageUrl || fallbackCoverImage;
    const coverTitle = eventData?.eventTitle || "Event";
    const chanceValue = calc( "now / 10 * 100", { now: get( selectedGuessOption, "nowChancePercentage", 0 ) } );
    const chanceText = `${chanceValue}% chance`;

    const selectedBetTypeDisplay = getBetTypeDisplay( selectedGuessOption?.betType, matchups );
    const positionTitleClass = selectedBetTypeDisplay.isNeutral ? "text-t1" : selectedBetTypeDisplay.textClass;
    const positionSubtitleClass = selectedBetTypeDisplay.isNeutral ? "text-t3" : selectedBetTypeDisplay.textClass;
    const chanceTextClass = selectedBetTypeDisplay.isNeutral ? "text-content-subtle" : selectedBetTypeDisplay.textClass;

    useLayoutEffect( () => {
        const textarea = textareaRef.current;
        if ( !textarea ) {
            return;
        }

        textarea.style.height = "auto";
        const nextHeight = Math.min( textarea.scrollHeight, COMMENT_TEXTAREA_MAX_HEIGHT );
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > COMMENT_TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
    }, [ content ] );

    return (
        <>
            {/* Position selector - above input area */}
            {shouldShowGuessSelector && selectedGuessOption && (
                <button
                    onClick={() => setDrawerOpen( true )}
                    className="w-full flex items-center gap-2 mb-3 text-left rounded-xl"
                >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="size-12 rounded-lg overflow-hidden bg-surface-subtle shrink-0">
                            <SmartImage
                                src={coverImage}
                                alt={coverTitle}
                                className="w-full h-full object-cover"
                                imageUrlOptions={{ width: 150, height: 150 }}
                            />
                        </div>
                        <div className="min-w-0 flex-1 text-t1">
                            <p
                                className={cn(
                                    "text-sm font-semibold leading-normal line-clamp-2",
                                    // positionTitleClass,
                                )}
                            >
                                {coverTitle}
                            </p>
                            <p
                                className={cn(
                                    "text-xs mt-1 truncate leading-[14px]", // positionSubtitleClass
                                )}
                            >
                                <span
                                    className={cn(
                                        selectedBetTypeDisplay.isNeutral ? "text-t3" : selectedBetTypeDisplay.textClass,
                                    )}
                                >
                                    {selectedBetTypeDisplay.label}
                                </span>
                                {selectedGuessOption.guessTitle && (
                                    <span
                                        // className={positionSubtitleClass}
                                    >
                                        {` · ${selectedGuessOption.guessTitle}`}
                                    </span>
                                )}
                                <span
                                    //  className={chanceTextClass}
                                >
                                    {` · ${chanceText}`}
                                </span>
                            </p>
                        </div>
                    </div>
                    <ChevronDownIcon className="w-6 h-6" />
                </button>
            )}

            {/* Input area */}
            <div
                className={cn(
                    "rounded-lg bg-surface-input transition-colors flex",
                    ( isFocused || content.trim().length > 0 )
                        ? "ring-1 ring-inset ring-action-primary"
                        : "ring-1 ring-inset ring-transparent",
                    content.trim().length > 0 ? "p-3" : "h-12 items-center px-3 py-2",
                )}
            >
                <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={( e ) => handleChange( e.target.value )}
                    onFocus={() => setIsFocused( true )}
                    onBlur={() => setIsFocused( false )}
                    placeholder="Add a comment..."
                    className="min-h-[1lh] w-full resize-none bg-transparent text-sm font-medium leading-normal text-t1 outline-none placeholder:font-medium placeholder:text-t3"
                    rows={1}
                />

                <div
                    className={cn(
                        "flex flex-col items-center gap-1 ml-3",
                        content.trim().length > 0 ? "justify-end" : "justify-center",
                    )}
                >
                    {content && <span className="text-xs font-normal text-t2">{remainingChars} left</span>}
                    <button
                        onClick={handlePost}
                        disabled={!content.trim() || isPosting}
                        className="inline-flex h-8 min-w-[58px] shrink-0 items-center justify-center rounded-lg bg-action-primary px-3 text-xs font-medium leading-normal text-content-fixed-inverse transition-colors disabled:cursor-not-allowed disabled:opacity-20"
                    >
                        {isPosting && <Loader2 className="animate-spin size-[1em]" />}
                        {isPosting ? "Posting..." : "Post"}
                    </button>
                </div>
            </div>

            {/* Position select drawer */}
            {shouldShowGuessSelector && (
                <PositionSelectDrawer
                    open={drawerOpen}
                    onOpenChange={setDrawerOpen}
                    eventImageUrl={eventData?.eventImageUrl}
                    eventTitle={eventData?.eventTitle || ""}
                    options={userGuessOptions || []}
                    selectedOption={selectedGuessOption}
                    onSelect={handleSelectGuessOption}
                    matchups={matchups}
                />
            )}
        </>
    );
}
